import { useEffect, useRef, useState } from 'react'
import {
  useDeleteProcedure,
  usePriorities,
  useProcedures,
  useProjectTemplates,
  useSaveProcedure,
  useSaveProjectTemplate,
  useTeams,
  useTemplateAction,
  useUsers
} from '../../api/queries'
import type { Procedure, ProjectTemplate, ProjectTemplateTask } from '../../api/types'
import { Card, EmptyState, ErrorBox, Loading } from '../../components/ui'

/** Ét underemne under redigering. `weight` er underemnets procent af sagen. */
interface TaskDraft {
  title: string
  description: string
  weight: number
  priorityId: string
  /** Fast ansvarlig: '' = vælges på sagen, 'u:<id>' = en person, 't:<id>' = et team (beslutning 142). */
  responsible: string
  deadlineOffsetDays: string
}

/** Et hovedemne med dets underemner. */
interface SectionDraft {
  title: string
  tasks: TaskDraft[]
}

function toDraft(task: ProjectTemplateTask): TaskDraft {
  return {
    title: task.title,
    description: task.description ?? '',
    weight: task.weight,
    priorityId: task.priority ? String(task.priority.id) : '',
    responsible: task.defaultAssignee
      ? `u:${task.defaultAssignee.id}`
      : task.defaultTeam
        ? `t:${task.defaultTeam.id}`
        : '',
    deadlineOffsetDays: task.deadlineOffsetDays === null || task.deadlineOffsetDays === undefined ? '' : String(task.deadlineOffsetDays)
  }
}

/**
 * Skabelonens underemner samlet under deres hovedemner, i den rækkefølge hovedemnerne
 * først optræder. Skabeloner fra før hovedemnerne lægges under "Opgaver", som kan omdøbes.
 */
function toSections(template: ProjectTemplate | null): SectionDraft[] {
  if (!template || template.tasks.length === 0) {
    return [{ title: '', tasks: [{ ...emptyTask, weight: 100 }] }]
  }
  const sections: SectionDraft[] = []
  for (const task of template.tasks) {
    const title = task.section?.trim() || 'Opgaver'
    const section = sections.find((s) => s.title === title)
    if (section) section.tasks.push(toDraft(task))
    else sections.push({ title, tasks: [toDraft(task)] })
  }
  return sections
}

const emptyTask: TaskDraft = {
  title: '',
  description: '',
  weight: 0,
  priorityId: '',
  responsible: '',
  deadlineOffsetDays: ''
}

/** 'u:5' -> person 5, 't:3' -> team 3. */
function parseResponsible(value: string): { defaultAssigneeId: number | null; defaultTeamId: number | null } {
  const [kind, id] = value.split(':')
  return {
    defaultAssigneeId: kind === 'u' ? Number(id) : null,
    defaultTeamId: kind === 't' ? Number(id) : null
  }
}

/** Fordeler 100 % ligeligt på n underemner - de første får den ekstra procent, så summen er præcis 100. */
function evenSplit(count: number): number[] {
  const base = Math.floor(100 / count)
  const rest = 100 - base * count
  return Array.from({ length: count }, (_, i) => base + (i < rest ? 1 : 0))
}

/**
 * Samme hovedemner, men med 100 % fordelt ligeligt på alle underemner. Bruges, hver gang
 * et underemne eller hovedemne kommer til eller fjernes, så summen altid er 100 % (Simon
 * 2026-10-28). En procent kan stadig rettes i hånden bagefter.
 */
function spread(sections: SectionDraft[]): SectionDraft[] {
  const count = sections.reduce((sum, s) => sum + s.tasks.length, 0)
  if (count === 0) return sections
  const split = evenSplit(Math.min(count, 100))
  let n = 0
  return sections.map((s) => ({ ...s, tasks: s.tasks.map((t) => ({ ...t, weight: split[n++] ?? 1 })) }))
}

/**
 * Procedurer og procedureskabeloner (beslutning 141).
 *
 * En procedure er en type sag ("Tavle", "Solceller"). Hver har en eller flere skabeloner
 * med hovedemner og underemner; underemnerne tæller i procent og giver 100 % tilsammen.
 * Projektleder og opefter må oprette, rette og slette.
 */
export function TemplatesPage() {
  const { data, isLoading, error } = useProjectTemplates(true)
  const { data: procedures } = useProcedures(true)
  const action = useTemplateAction()
  const [editing, setEditing] = useState<ProjectTemplate | 'new' | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  // Skabelonerne grupperet under deres procedure; dem uden procedure til sidst.
  const groups: { procedure: Procedure | null; templates: ProjectTemplate[] }[] = [
    ...(procedures ?? []).map((procedure) => ({
      procedure,
      templates: (data ?? []).filter((t) => t.procedureId === procedure.id)
    })),
    { procedure: null, templates: (data ?? []).filter((t) => !t.procedureId) }
  ].filter((g) => g.procedure !== null || g.templates.length > 0)

  return (
    <div className="stack" style={{ gap: 16 }}>
      <p className="small muted" style={{ margin: 0 }}>
        En <b>procedure</b> er en type sag, f.eks. "Tavle" eller "Solceller". Hver procedure har en eller flere{' '}
        <b>skabeloner</b> med hovedemner og underemner. Underemnerne tæller i procent og skal give 100 % tilsammen - det
        er dem, sagens fremdrift regnes ud fra. Ændringer her påvirker ikke sager, der allerede er oprettet.
      </p>

      <ProceduresCard procedures={procedures ?? []} />

      <div className="row row--between">
        <h2 style={{ margin: 0, fontSize: 18 }}>Skabeloner</h2>
        <button
          className="btn btn--primary"
          disabled={!procedures || procedures.length === 0}
          title={!procedures || procedures.length === 0 ? 'Opret først en procedure' : undefined}
          onClick={() => setEditing('new')}
        >
          Ny skabelon
        </button>
      </div>

      {/* En ny skabelon laves her; en eksisterende rettes på sin egen plads i listen. */}
      {editing === 'new' && (
        <TemplateForm
          template={null}
          procedures={procedures ?? []}
          onClose={() => setEditing(null)}
        />
      )}

      {actionError && <div className="error-box">{actionError}</div>}

      {data && data.length === 0 && !editing && <EmptyState>Der er ingen procedureskabeloner endnu.</EmptyState>}

      {groups.map((group) => (
        <div key={group.procedure?.id ?? 'none'} className="stack" style={{ gap: 10 }}>
          <h3 style={{ margin: '8px 0 0', fontSize: 16 }}>
            {group.procedure?.name ?? 'Uden procedure'}
            {group.procedure && !group.procedure.isActive && <span className="small muted"> (skjult)</span>}
          </h3>
          {group.procedure === null && (
            <p className="tiny warn" style={{ margin: 0 }}>
              Skabeloner fra før procedurerne. Tryk "Rediger", vælg en procedure og del underemnerne op i hovedemner.
            </p>
          )}
          {group.templates.length === 0 && <p className="small muted" style={{ margin: 0 }}>Ingen skabeloner endnu.</p>}

          {group.templates.map((template) =>
            editing !== 'new' && editing?.id === template.id ? (
              <TemplateForm
                key={template.id}
                template={template}
                procedures={procedures ?? []}
                onClose={() => setEditing(null)}
              />
            ) : (
            <Card
              key={template.id}
              title={template.name}
              actions={
                <div className="btn-group">
                  <span
                    className="pill"
                    style={{
                      background: template.isActive ? '#DCFCE7' : '#F1F5F9',
                      color: template.isActive ? '#166534' : '#64748B'
                    }}
                  >
                    {template.isActive ? 'Aktiv' : 'Skjult'}
                  </span>
                  <button className="btn btn--sm" onClick={() => setEditing(template)}>
                    Rediger
                  </button>
                  <button
                    className="btn btn--sm"
                    disabled={action.isPending}
                    onClick={() => action.mutate({ id: template.id, action: 'duplicate' })}
                  >
                    Kopiér
                  </button>
                  <button
                    className="btn btn--sm btn--danger"
                    disabled={action.isPending}
                    onClick={() => {
                      if (window.confirm(`Slet skabelonen "${template.name}"? Sager oprettet fra den påvirkes ikke.`)) {
                        setActionError(null)
                        action.mutate(
                          { id: template.id, action: 'delete' },
                          { onError: (err) => setActionError(err instanceof Error ? err.message : 'Kunne ikke slette.') }
                        )
                      }
                    }}
                  >
                    Slet
                  </button>
                </div>
              }
            >
              {template.description && <p className="small muted" style={{ marginTop: 0 }}>{template.description}</p>}

              <div className="row small muted" style={{ flexWrap: 'wrap', marginBottom: 10 }}>
                <span>{template.taskCount} underemner</span>
                {template.category && <span>· Kategori: {template.category}</span>}
                {template.defaultPriority && <span>· Prioritet: {template.defaultPriority.name}</span>}
                {template.defaultDurationDays && <span>· Varighed: {template.defaultDurationDays} dage</span>}
              </div>

              <TemplateOutline template={template} />
            </Card>
            )
          )}
        </div>
      ))}
    </div>
  )
}

/** Skabelonens hovedemner og underemner med procent - også brugt ved "Ny sag". */
export function TemplateOutline({ template }: { template: ProjectTemplate }) {
  const sections: { title: string; tasks: ProjectTemplateTask[] }[] = []
  for (const task of template.tasks) {
    const title = task.section?.trim() || 'Opgaver'
    const section = sections.find((s) => s.title === title)
    if (section) section.tasks.push(task)
    else sections.push({ title, tasks: [task] })
  }
  const total = template.tasks.reduce((sum, t) => sum + t.weight, 0)

  return (
    <div className="small">
      {sections.map((section, index) => (
        <div key={index} style={{ marginBottom: 6 }}>
          <div style={{ fontWeight: 600 }}>
            {section.title}{' '}
            <span className="muted" style={{ fontWeight: 400 }}>
              ({section.tasks.reduce((sum, t) => sum + t.weight, 0)} %)
            </span>
          </div>
          <ul style={{ margin: '2px 0 0', paddingLeft: 20 }}>
            {section.tasks.map((task) => (
              <li key={task.id}>
                {task.title} <span className="muted">· {task.weight} %</span>
                {task.defaultTeam && <span className="muted"> · 👷 {task.defaultTeam.name}</span>}
                {task.defaultAssignee && <span className="muted"> · {task.defaultAssignee.displayName}</span>}
              </li>
            ))}
          </ul>
        </div>
      ))}
      {total !== 100 && (
        <div className="tiny warn">Underemnerne giver {total} % - ikke 100 %. Ret skabelonen.</div>
      )}
    </div>
  )
}

/** Listen over procedurer: opret, omdøb, skjul og slet. */
function ProceduresCard({ procedures }: { procedures: Procedure[] }) {
  const save = useSaveProcedure()
  const remove = useDeleteProcedure()
  const [newName, setNewName] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function run(work: () => Promise<unknown>) {
    setError(null)
    try {
      await work()
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Det lykkedes ikke.')
      return false
    }
  }

  return (
    <Card title="Procedurer">
      {procedures.length === 0 && (
        <p className="small muted" style={{ marginTop: 0 }}>
          Ingen procedurer endnu. Opret den første, f.eks. "Tavle" - så kan du lave skabeloner til den.
        </p>
      )}

      <div className="stack" style={{ gap: 6, marginBottom: 12 }}>
        {procedures.map((procedure) =>
          editingId === procedure.id ? (
            <form
              key={procedure.id}
              className="row"
              onSubmit={async (e) => {
                e.preventDefault()
                const ok = await run(() =>
                  save.mutateAsync({
                    id: procedure.id,
                    name: editName,
                    description: procedure.description ?? null,
                    isActive: procedure.isActive,
                    sortOrder: procedure.sortOrder
                  })
                )
                if (ok) setEditingId(null)
              }}
            >
              <input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)} />
              <button className="btn btn--sm btn--primary" type="submit" disabled={save.isPending}>
                Gem
              </button>
              <button className="btn btn--sm" type="button" onClick={() => setEditingId(null)}>
                Fortryd
              </button>
            </form>
          ) : (
            <div key={procedure.id} className="row row--between" style={{ gap: 8 }}>
              <span>
                <b>{procedure.name}</b>{' '}
                <span className="small muted">
                  · {procedure.templateCount} {procedure.templateCount === 1 ? 'skabelon' : 'skabeloner'}
                  {!procedure.isActive && ' · skjult'}
                </span>
              </span>
              <div className="btn-group">
                <button
                  className="btn btn--sm"
                  onClick={() => {
                    setEditingId(procedure.id)
                    setEditName(procedure.name)
                  }}
                >
                  Omdøb
                </button>
                <button
                  className="btn btn--sm"
                  disabled={save.isPending}
                  onClick={() =>
                    run(() =>
                      save.mutateAsync({
                        id: procedure.id,
                        name: procedure.name,
                        description: procedure.description ?? null,
                        isActive: !procedure.isActive,
                        sortOrder: procedure.sortOrder
                      })
                    )
                  }
                >
                  {procedure.isActive ? 'Skjul' : 'Vis'}
                </button>
                <button
                  className="btn btn--sm btn--danger"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm(`Slet proceduren "${procedure.name}"?`)) {
                      run(() => remove.mutateAsync(procedure.id))
                    }
                  }}
                >
                  Slet
                </button>
              </div>
            </div>
          )
        )}
      </div>

      <form
        className="row"
        onSubmit={async (e) => {
          e.preventDefault()
          if (!newName.trim()) return
          const ok = await run(() =>
            save.mutateAsync({ name: newName, description: null, isActive: true, sortOrder: procedures.length * 10 })
          )
          if (ok) setNewName('')
        }}
      >
        <input placeholder="Ny procedure, f.eks. Tavle" value={newName} onChange={(e) => setNewName(e.target.value)} />
        <button className="btn btn--sm btn--primary" type="submit" disabled={save.isPending || !newName.trim()}>
          + Opret procedure
        </button>
      </form>

      {error && <div className="error-box" style={{ marginTop: 10 }}>{error}</div>}
    </Card>
  )
}

function TemplateForm({
  template,
  procedures,
  onClose
}: {
  template: ProjectTemplate | null
  procedures: Procedure[]
  onClose: () => void
}) {
  const save = useSaveProjectTemplate(template?.id)
  const { data: priorities } = usePriorities()
  const { data: users } = useUsers()
  const { data: teams } = useTeams()

  const [form, setForm] = useState({
    name: template?.name ?? '',
    description: template?.description ?? '',
    category: template?.category ?? '',
    procedureId: template?.procedureId ? String(template.procedureId) : procedures.length === 1 ? String(procedures[0].id) : '',
    defaultPriorityId: template?.defaultPriority ? String(template.defaultPriority.id) : '',
    defaultDurationDays: template?.defaultDurationDays ? String(template.defaultDurationDays) : '',
    isActive: template?.isActive ?? true,
    sortOrder: template?.sortOrder ?? 0
  })
  const [sections, setSections] = useState<SectionDraft[]>(() => toSections(template))
  const [error, setError] = useState<string | null>(null)
  const top = useRef<HTMLDivElement>(null)

  // Formularen kan åbne langt nede i listen - rul den frem, så man ser, at der skete noget.
  useEffect(() => {
    top.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const total = sections.reduce((sum, s) => sum + s.tasks.reduce((x, t) => x + (t.weight || 0), 0), 0)

  function updateSection(index: number, changes: Partial<SectionDraft>) {
    setSections((current) => current.map((s, i) => (i === index ? { ...s, ...changes } : s)))
  }

  function updateTask(sectionIndex: number, taskIndex: number, changes: Partial<TaskDraft>) {
    setSections((current) =>
      current.map((s, i) =>
        i === sectionIndex ? { ...s, tasks: s.tasks.map((t, j) => (j === taskIndex ? { ...t, ...changes } : t)) } : s
      )
    )
  }

  function moveSection(index: number, direction: -1 | 1) {
    const target = index + direction
    if (target < 0 || target >= sections.length) return
    setSections((current) => {
      const next = [...current]
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })
  }

  function moveTask(sectionIndex: number, taskIndex: number, direction: -1 | 1) {
    setSections((current) =>
      current.map((s, i) => {
        if (i !== sectionIndex) return s
        const target = taskIndex + direction
        if (target < 0 || target >= s.tasks.length) return s
        const tasks = [...s.tasks]
        ;[tasks[taskIndex], tasks[target]] = [tasks[target], tasks[taskIndex]]
        return { ...s, tasks }
      })
    )
  }

  function spreadEvenly() {
    setSections((current) => spread(current))
  }

  // Tilføj og fjern fordeler procenterne igen, så summen altid er 100 %.
  function addTask(sectionIndex: number) {
    setSections((current) =>
      spread(current.map((s, i) => (i === sectionIndex ? { ...s, tasks: [...s.tasks, { ...emptyTask }] } : s)))
    )
  }

  function removeTask(sectionIndex: number, taskIndex: number) {
    setSections((current) =>
      spread(current.map((s, i) => (i === sectionIndex ? { ...s, tasks: s.tasks.filter((_, j) => j !== taskIndex) } : s)))
    )
  }

  function addSection() {
    setSections((current) => spread([...current, { title: '', tasks: [{ ...emptyTask }] }]))
  }

  function removeSection(sectionIndex: number) {
    setSections((current) => spread(current.filter((_, i) => i !== sectionIndex)))
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)

    // Tomme rækker smides IKKE væk i stilhed: de tæller med i "I alt", så forsvandt de ved
    // gem, gav resten under 100 % uden at man kunne se hvorfor (Simon fik "de giver 91 %").
    // I stedet siges det præcist, hvor den tomme række står.
    const cleaned = sections.map((s) => ({ title: s.title.trim(), tasks: s.tasks }))

    if (cleaned.every((s) => s.tasks.length === 0)) {
      setError('Skabelonen skal have mindst ét underemne.')
      return
    }
    for (const [i, section] of cleaned.entries()) {
      const where = section.title ? `under "${section.title}"` : `under hovedemne nr. ${i + 1}`
      if (section.tasks.length === 0) {
        setError(`Hovedemne nr. ${i + 1}${section.title ? ` ("${section.title}")` : ''} har ingen underemner - tilføj et, eller fjern hovedemnet med ×.`)
        return
      }
      if (!section.title) {
        setError(`Hovedemne nr. ${i + 1} har ikke noget navn - skriv et, eller fjern det med ×.`)
        return
      }
      const empty = section.tasks.findIndex((t) => t.title.trim().length === 0)
      if (empty >= 0) {
        setError(`Underemne nr. ${empty + 1} ${where} har ikke noget navn - skriv et, eller fjern det med ×.`)
        return
      }
    }
    const sum = cleaned.reduce((x, s) => x + s.tasks.reduce((y, t) => y + (t.weight || 0), 0), 0)
    if (sum !== 100) {
      setError(`Underemnerne skal give 100 % tilsammen - de giver ${sum} %.`)
      return
    }
    if (!form.procedureId) {
      setError('Vælg hvilken procedure skabelonen hører til.')
      return
    }

    try {
      await save.mutateAsync({
        name: form.name,
        description: form.description || null,
        category: form.category || null,
        procedureId: Number(form.procedureId),
        defaultPriorityId: form.defaultPriorityId ? Number(form.defaultPriorityId) : null,
        defaultDurationDays: form.defaultDurationDays ? Number(form.defaultDurationDays) : null,
        isActive: form.isActive,
        sortOrder: form.sortOrder,
        tasks: cleaned.flatMap((section) =>
          section.tasks.map((task) => ({
            section: section.title,
            title: task.title,
            description: task.description || null,
            weight: task.weight,
            priorityId: task.priorityId ? Number(task.priorityId) : null,
            ...parseResponsible(task.responsible),
            deadlineOffsetDays: task.deadlineOffsetDays === '' ? null : Number(task.deadlineOffsetDays)
          }))
        )
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Skabelonen kunne ikke gemmes.')
    }
  }

  const totalColor = total === 100 ? '#15803D' : '#DC2626'

  return (
    <div ref={top} style={{ scrollMarginTop: 80 }}>
    <Card title={template ? `Rediger ${template.name}` : 'Ny procedureskabelon'}>
      <form onSubmit={submit}>
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Procedure *</span>
            <select required value={form.procedureId} onChange={(e) => setForm({ ...form, procedureId: e.target.value })}>
              <option value="">Vælg procedure</option>
              {procedures.map((procedure) => (
                <option key={procedure.id} value={procedure.id}>
                  {procedure.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field__label">Navn *</span>
            <input
              required
              placeholder="F.eks. Ny tavleinstallation"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="field__label">Kategori (kopieres til sagen)</span>
            <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          </label>
          <label className="field">
            <span className="field__label">Standardprioritet</span>
            <select value={form.defaultPriorityId} onChange={(e) => setForm({ ...form, defaultPriorityId: e.target.value })}>
              <option value="">Systemets standard</option>
              {priorities?.map((priority) => (
                <option key={priority.id} value={priority.id}>
                  {priority.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field__label">Forventet varighed (dage)</span>
            <input
              type="number"
              min={1}
              value={form.defaultDurationDays}
              onChange={(e) => setForm({ ...form, defaultDurationDays: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="field__label">Status</span>
            <select value={form.isActive ? 'true' : 'false'} onChange={(e) => setForm({ ...form, isActive: e.target.value === 'true' })}>
              <option value="true">Aktiv - kan vælges ved oprettelse</option>
              <option value="false">Skjult</option>
            </select>
          </label>
          <label className="field">
            <span className="field__label">Sortering</span>
            <input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })} />
          </label>
        </div>

        <label className="field">
          <span className="field__label">Beskrivelse</span>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </label>

        <div className="field">
          <div className="row row--between" style={{ gap: 8, flexWrap: 'wrap' }}>
            <span className="field__label">Hovedemner og underemner</span>
            <span className="row" style={{ gap: 8 }}>
              <b style={{ color: totalColor }}>
                I alt {total} %{total !== 100 && (total < 100 ? ` - mangler ${100 - total} %` : ` - ${total - 100} % for meget`)}
              </b>
              <button className="btn btn--sm" type="button" onClick={spreadEvenly}>
                Fordel ligeligt
              </button>
            </span>
          </div>
          <p className="tiny muted" style={{ margin: '0 0 8px' }}>
            Hvert underemne tæller en procent af sagen, og alle underemner skal give 100 % tilsammen. "Dag" er antal dage
            efter sagens startdato, underemnet skal være færdigt - tomt for ingen deadline.
          </p>

          <div className="stack" style={{ gap: 12 }}>
            {sections.map((section, sectionIndex) => {
              const sectionSum = section.tasks.reduce((sum, t) => sum + (t.weight || 0), 0)
              return (
                <div key={sectionIndex} className="card card--flat" style={{ padding: 12 }}>
                  <div className="row" style={{ marginBottom: 10 }}>
                    <span className="tiny muted" style={{ minWidth: 22 }}>{sectionIndex + 1}.</span>
                    <input
                      style={{ fontWeight: 600 }}
                      placeholder="Hovedemne, f.eks. Tavle"
                      value={section.title}
                      onChange={(e) => updateSection(sectionIndex, { title: e.target.value })}
                    />
                    <span className="small muted" style={{ whiteSpace: 'nowrap' }}>{sectionSum} %</span>
                    <button className="btn btn--sm" type="button" disabled={sectionIndex === 0} onClick={() => moveSection(sectionIndex, -1)}>
                      ↑
                    </button>
                    <button
                      className="btn btn--sm"
                      type="button"
                      disabled={sectionIndex === sections.length - 1}
                      onClick={() => moveSection(sectionIndex, 1)}
                    >
                      ↓
                    </button>
                    <button
                      className="btn btn--sm btn--danger"
                      type="button"
                      title="Fjern hovedemnet med dets underemner"
                      onClick={() => removeSection(sectionIndex)}
                    >
                      ×
                    </button>
                  </div>

                  <div className="stack" style={{ gap: 8, paddingLeft: 22 }}>
                    {section.tasks.map((task, taskIndex) => (
                      <div key={taskIndex} style={{ borderLeft: '3px solid #E2E8F0', paddingLeft: 10 }}>
                        <div className="row" style={{ marginBottom: 6 }}>
                          <input
                            placeholder="Underemne"
                            value={task.title}
                            onChange={(e) => updateTask(sectionIndex, taskIndex, { title: e.target.value })}
                          />
                          <input
                            type="number"
                            min={1}
                            max={100}
                            aria-label="Procent"
                            style={{ width: 72 }}
                            value={task.weight || ''}
                            onChange={(e) => updateTask(sectionIndex, taskIndex, { weight: Number(e.target.value) })}
                          />
                          <span className="small muted">%</span>
                          <button
                            className="btn btn--sm"
                            type="button"
                            disabled={taskIndex === 0}
                            onClick={() => moveTask(sectionIndex, taskIndex, -1)}
                          >
                            ↑
                          </button>
                          <button
                            className="btn btn--sm"
                            type="button"
                            disabled={taskIndex === section.tasks.length - 1}
                            onClick={() => moveTask(sectionIndex, taskIndex, 1)}
                          >
                            ↓
                          </button>
                          <button
                            className="btn btn--sm btn--danger"
                            type="button"
                            onClick={() => removeTask(sectionIndex, taskIndex)}
                          >
                            ×
                          </button>
                        </div>

                        <div className="form-grid">
                          <label className="field">
                            <span className="field__label">Fast ansvarlig</span>
                            <select
                              value={task.responsible}
                              onChange={(e) => updateTask(sectionIndex, taskIndex, { responsible: e.target.value })}
                            >
                              <option value="">Vælges på sagen</option>
                              {/* Et team: alle på holdet ser opgaven, og den første tager den (beslutning 142). */}
                              {teams && teams.some((t) => t.isActive) && (
                                <optgroup label="Teams">
                                  {teams
                                    .filter((team) => team.isActive || `t:${team.id}` === task.responsible)
                                    .map((team) => (
                                      <option key={`t${team.id}`} value={`t:${team.id}`}>
                                        👷 {team.name} ({team.memberCount})
                                      </option>
                                    ))}
                                </optgroup>
                              )}
                              <optgroup label="Personer">
                                {users?.map((user) => (
                                  <option key={`u${user.id}`} value={`u:${user.id}`}>
                                    {user.displayName}
                                  </option>
                                ))}
                              </optgroup>
                            </select>
                          </label>
                          <label className="field">
                            <span className="field__label">Dag (efter start)</span>
                            <input
                              type="number"
                              min={0}
                              value={task.deadlineOffsetDays}
                              onChange={(e) => updateTask(sectionIndex, taskIndex, { deadlineOffsetDays: e.target.value })}
                            />
                          </label>
                          <label className="field">
                            <span className="field__label">Prioritet</span>
                            <select
                              value={task.priorityId}
                              onChange={(e) => updateTask(sectionIndex, taskIndex, { priorityId: e.target.value })}
                            >
                              <option value="">Normal</option>
                              {priorities?.map((priority) => (
                                <option key={priority.id} value={priority.id}>
                                  {priority.name}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                      </div>
                    ))}

                    <button
                      className="btn btn--sm"
                      type="button"
                      style={{ alignSelf: 'flex-start' }}
                      onClick={() => addTask(sectionIndex)}
                    >
                      + Tilføj underemne
                    </button>
                  </div>
                </div>
              )
            })}
          </div>

          <button
            className="btn btn--sm"
            type="button"
            style={{ marginTop: 10 }}
            onClick={addSection}
          >
            + Tilføj hovedemne
          </button>
        </div>

        {error && <div className="error-box" style={{ marginBottom: 10 }}>{error}</div>}

        <div className="btn-group">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Gemmer ...' : 'Gem skabelon'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Fortryd
          </button>
        </div>
      </form>
    </Card>
    </div>
  )
}
